import constants, {SVG_ICON_NAME} from "../../../../../../appConstants";
import { NumericEditor } from "../../../../../dataGrid/cellEditors";
import {
  SpecialNeedsRenderer,
  getSpecialNeeds,
} from "../../../../../dataGrid/cellRenderer/index";
import {
  mergeUserConfig,
  formatMonthDateYearWithoutUTC,
} from "../../../../../dataGrid/gridUtilities";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from "../../../../../dataGrid/columnFilters";
import PickupTypesRenderer from "./cellRenderer/PickupTypesRenderer";
import PickupNumberRenderer from "./cellRenderer/PickupNumberCellRenderer";
import { getZoneValue } from "../../utils/index";
import PickupIndicatorIconRenderer from "./cellRenderer/PickupIndicatorIconRenderer";
import { updateGridColumnHeaderNames } from "../../../utils/utils";
import { gridShortHeaderNamesConfig } from "./constants";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({
    userConfig,
    showStaticRoute,
    featureFlags: { enableColumnShortForm },
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration(showStaticRoute);
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig,
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm,
    });
  }

  static PickupTypeValueGetter = (params) => {
    const { data } = params;
    let pickupTypes = data.priority ? [...data.priority] : [];
    if (data.is3PlPickup) {
      pickupTypes.push(SVG_ICON_NAME._3PL);
    }
    if (data.isSwapPickup) {
      pickupTypes.push(SVG_ICON_NAME.SWAP);
    }
    if (data.isDailyPickup) {
      pickupTypes.push(SVG_ICON_NAME.dailyPickup);
    }
    return pickupTypes.filter(Boolean);
  };

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getPickupTypeOptions(params) {
    return params.context.pickupTypeOptions;
  }

  static getNonZeroValue = (params) => {
    return params.data[params.column.colId] || "";
  };

  static getRouteNameColumns(showStaticRoute) {
    const driverRouteColumns = {
      headerName: "Driver Route",
      headerTooltip: "Driver Route Name",
      field: "routeName",
      minWidth: constants.columnWidth.DRIVER_ROUTE,
      cellClass: "gridCell-leftAlign",
      filterFramework: MultiSelectFilter,
      hide: true,
    };
    if (showStaticRoute) {
      return [
        {
          headerName: "Route",
          headerTooltip: "Route Name",
          field: "staticRoute",
          minWidth: constants.columnWidth.W_90,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        { ...driverRouteColumns },
      ];
    }
    return [
      {
        ...driverRouteColumns,
        hide: false,
      },
    ];
  }

  static getDefaultConfiguration(showStaticRoute) {
    const routeNameColumns = GridConfig.getRouteNameColumns(showStaticRoute);
    return {
      title: constants.gridNames.PickupsGrid,
      keyColumn: "pickupId",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        //  cellEditorFramework: TextFieldEditor,
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          maxWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          pinned: "left",
          field: "isMergedPickup",
          lockPosition: true,
          cellClass: ["gridCell-centerAlign", "gridCell-merge"],
          filter: false,
          cellRendererFramework: PickupIndicatorIconRenderer,
          isInvisibleInShowHide: true,
        },
        {
          headerName: "Pickup Number",
          headerTooltip: "Pickup Number",
          field: "pickupNumber",
          minWidth: constants.columnWidth.PICKUP_NUMBER,
          cellClass: "gridCell-leftAlign",
          pinned: "left",
          lockPosition: true,
          filterFramework: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
          cellRendererFramework: PickupNumberRenderer,
        },
        {
          headerName: "Zone",
          headerTooltip: "Zone",
          valueGetter: getZoneValue,
          minWidth: constants.columnWidth.DISPATCH_ZONE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Driver",
          headerTooltip: "Driver Name",
          field: "driverName",
          minWidth: constants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        ...routeNameColumns,
        {
          headerName: "Type",
          headerTooltip: "Type",
          valueGetter: GridConfig.PickupTypeValueGetter,
          minWidth: constants.columnWidth.PICKUP_TYPE,
          cellClass: "gridCell-leftAlign",
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getPickupTypeOptions,
          },
          cellRendererFramework: PickupTypesRenderer,
        },
        {
          headerName: "Shipper Name",
          headerTooltip: "Shipper Name",
          field: "shipperName",
          minWidth: constants.columnWidth.CONSIGNEE_NAME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "shipperName",
        },
        {
          headerName: "Address",
          field: "shipperAddress",
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          headerTooltip: "Shipper Address",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "shipperAddress",
        },
        {
          headerName: "City",
          field: "shipperCity",
          headerTooltip: "Shipper City",
          minWidth: constants.columnWidth.CITY,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "State",
          field: "shipperState",
          headerTooltip: "Shipper State",
          minWidth: constants.columnWidth.STATE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Zip",
          field: "shipperZip",
          headerTooltip: "Shipper Zip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Pickup Date",
          field: "pickupDate",
          headerTooltip: "Pickup Date",
          minWidth: constants.columnWidth.PICKUP_DATE,
          cellClass: "gridCell-leftAlign",
          valueFormatter: (params) => formatMonthDateYearWithoutUTC(params),
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.MONTHDATEYEAR,
          },
        },
        {
          headerName: "Ready Time",
          field: "pickupReadyTime",
          headerTooltip: "Ready Time",
          minWidth: constants.columnWidth.W_120,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Close Time",
          field: "pickupCloseTime",
          headerTooltip: "Close Time",
          minWidth: constants.columnWidth.W_115,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Spl Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: getSpecialNeeds,
          headerTooltip: "Special Needs",
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: SpecialNeedsRenderer,
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
        },
        {
          headerName: "Bills",
          field: "bills",
          minWidth: constants.columnWidth.BILLS,
          headerTooltip: "Bills",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Pieces",
          field: "pieces",
          minWidth: constants.columnWidth.W_85,
          headerTooltip: "Pieces",
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.getNonZeroValue,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "HU",
          field: "hu",
          minWidth: constants.columnWidth.W_80,
          headerTooltip: "Handling Unit",
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.getNonZeroValue,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Pkg Type",
          field: "",
          minWidth: constants.columnWidth.PKG_TYPE,
          headerTooltip: "Package Type",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Weight",
          field: "weight",
          minWidth: constants.columnWidth.W_90,
          headerTooltip: "Weight",
          cellClass: "gridCell-centerAlign",
          cellEditorFramework: NumericEditor,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Special Instruction",
          field: "specialInstructions",
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: "Special Instructions",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "specialInstructions",
        },
        {
          headerName: "Driver Number",
          headerTooltip: "Driver Number",
          field: "driverNumber",
          minWidth: constants.columnWidth.DRIVER_NUMBER,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
        },
      ],
    };
  }
}
